// All new components must import useTranslation and use t() for UI strings
import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import { CONSTRUCTION_TYPES, WORK_NATURES } from '../../../constants/projectOptions';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import { listUsersByRole } from '../../../services/userManagementService';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';

// Shared input class — matches AddContractModal design system
const inputCls =
  'w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none ' + 'focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-colors '+ 'placeholder:text-gray-400 text-gray-900';

const readOnlyCls =
  'w-full px-3 py-2 text-xs border border-gray-100 rounded-lg bg-gray-50 text-gray-500 cursor-default select-none';

const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5';

const NewProjectModal = ({ isOpen, onClose, onCreate, initialData = null, isEditMode = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    projectNumber: '',
    clientProjectNumber: '',
    clientName: '',
    startDate: '',
    endDate: '',
    status: 'active',
    constructionType: '',
    workNature: '',
    architect: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    description: '',
    pmUserId: null,
    // After MIG 2: renamed from superintendentUserId to supervisorUserId
    supervisorUserId: null,
    coordinatorUserId: null,
    assistantUserId: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Role-filtered user lists for team dropdowns
  const [pmUsers, setPmUsers] = useState([]);
  const [supervisorUsers, setSupervisorUsers] = useState([]);
  const [coordinatorUsers, setCoordinatorUsers] = useState([]);
  const [assistantUsers, setAssistantUsers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  // PortalDropdown open state for each team field
  const [openTeamDropdown, setOpenTeamDropdown] = useState(null); // 'pm' | 'supervisor' | 'coordinator' | 'assistant' | null
  const pmBtnRef = useRef(null);
  const supervisorBtnRef = useRef(null);
  const coordinatorBtnRef = useRef(null);
  const assistantBtnRef = useRef(null);

  // Fetch role-filtered users when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchTeamUsers = async () => {
      setTeamLoading(true);
      try {
        const [pmRes, supRes, coordRes, asstRes] = await Promise.all([
          listUsersByRole('pm'),
          listUsersByRole('supervisor'),
          listUsersByRole('coordinator'),
          listUsersByRole('assistant'),
        ]);
        setPmUsers(pmRes?.data ?? []);
        setSupervisorUsers(supRes?.data ?? []);
        setCoordinatorUsers(coordRes?.data ?? []);
        setAssistantUsers(asstRes?.data ?? []);
      } catch (err) {
        console.error('[NewProjectModal] fetchTeamUsers error:', err);
      } finally {
        setTeamLoading(false);
      }
    };
    fetchTeamUsers();
  }, [isOpen]);

  // Compute project timeframe in days from start/end dates
  const computeTimeframe = () => {
    if (!formData?.startDate || !formData?.endDate) return null;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffMs = end - start;
    if (diffMs <= 0) return null;
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  // Pre-fill form when in edit mode
  useEffect(() => {
    if (isEditMode && initialData && isOpen) {
      setFormData({
        name: initialData?.name || '',
        projectNumber: initialData?.projectNumber || '',
        clientProjectNumber: initialData?.clientProjectNumber || '',
        clientName: initialData?.clientName || '',
        startDate: initialData?.startDate || '',
        endDate: initialData?.endDate || '',
        status: initialData?.status || 'active',
        constructionType: initialData?.constructionType || '',
        workNature: initialData?.workNature || '',
        architect: initialData?.architect || '',
        address: initialData?.address || '',
        city: initialData?.city || '',
        state: initialData?.state || '',
        zipCode: initialData?.zipCode || '',
        description: initialData?.description || '',
        pmUserId: initialData?.pmUserId || null,
        // After MIG 2: support both old (superintendentUserId) and new (supervisorUserId) field names
        supervisorUserId: initialData?.supervisorUserId ?? initialData?.superintendentUserId ?? null,
        // Restore coordinator from legacy DB data
        coordinatorUserId: initialData?.coordinatorUserId || null,
        assistantUserId: initialData?.assistantUserId || null
      });
    } else if (!isEditMode && isOpen) {
      setFormData({
        name: '',
        projectNumber: '',
        clientProjectNumber: '',
        clientName: '',
        startDate: '',
        endDate: '',
        status: 'active',
        constructionType: '',
        workNature: '',
        architect: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        description: '',
        pmUserId: null,
        supervisorUserId: null,
        coordinatorUserId: null,
        assistantUserId: null
      });
    }
  }, [isEditMode, initialData, isOpen]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = t('newProjectModal.projectNameRequired');
    }
    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData?.endDate) {
      newErrors.endDate = 'End date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log("CREATE PROJECT CLICKED");
    console.log("🔵 Submit clicked");
    console.log("🧪 Running validation");
    if (validateForm()) {
      if (typeof onCreate !== 'function') {
        console.error('❌ onCreate prop is not defined');
        return;
      }
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData?.name,
          projectNumber: formData?.projectNumber?.trim() || null,
          clientProjectNumber: formData?.clientProjectNumber?.trim() || null,
          clientName: formData?.clientName || null,
          startDate: formData?.startDate || null,
          endDate: formData?.endDate || null,
          status: formData?.status,
          constructionType: formData?.constructionType || null,
          workNature: formData?.workNature || null,
          architect: formData?.architect || null,
          address: formData?.address || null,
          city: formData?.city || null,
          state: formData?.state || null,
          zipCode: formData?.zipCode || null,
          description: formData?.description || null,
          pmUserId: formData?.pmUserId || null,
          // After MIG 2: use supervisorUserId (renamed from superintendentUserId)
          supervisorUserId: formData?.supervisorUserId || null,
          // coordinator_user_id: reinstated — writes to the existing DB column
          coordinatorUserId: formData?.coordinatorUserId || null,
          // Write to assistant_user_id
          assistantUserId: formData?.assistantUserId || null
        };
        console.log("📤 Calling onCreate with:", payload);
        await onCreate(payload);
        console.log("✅ onCreate resolved");
        handleClose();
      } catch (error) {
        console.error("🔥 Supabase error:", error);
        // Show friendly error for duplicate project number
        if (error?.code === '23505' || error?.message?.includes('projects_org_project_number_unique')) {
          setErrors({ projectNumber: t('newProjectModal.projectNumberDuplicate') });
        } else {
          alert(error?.message);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log("❌ Validation failed");
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      projectNumber: '',
      clientProjectNumber: '',
      clientName: '',
      startDate: '',
      endDate: '',
      status: 'active',
      constructionType: '',
      workNature: '',
      architect: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      description: '',
      pmUserId: null,
      supervisorUserId: null,
      coordinatorUserId: null,
      assistantUserId: null
    });
    setErrors({});
    setOpenTeamDropdown(null);
    onClose();
  };

  useEscapeKey(() => { handleClose(); }, !!isOpen && !isSubmitting);

  if (!isOpen) return null;

  const timeframe = computeTimeframe();

  const constructionTypeOptions = CONSTRUCTION_TYPES?.map(type => ({
    value: type,
    label: type,
    searchText: type?.toLowerCase()
  }));

  const workNatureOptions = WORK_NATURES?.map(nature => ({
    value: nature,
    label: nature,
    searchText: nature?.toLowerCase()
  }));

  // Helper: get display label for a user id from a list
  const getUserLabel = (userId, userList) => {
    if (!userId) return null;
    const user = userList?.find(u => u?.id === userId);
    return user ? (user?.full_name || user?.email) : null;
  };

  // Shared trigger button style — matches inputCls visually with orange focus ring
  const triggerCls =
    'w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none ' + 'bg-white transition-colors text-left flex items-center justify-between gap-1 '+ 'hover:border-primary/50 focus:border-primary focus:ring-2 focus:ring-primary/20 '+ 'cursor-pointer';

  const inputCls =
    'w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none ' + 'focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-colors '+ 'placeholder:text-gray-400 text-gray-900';

  const readOnlyCls =
    'w-full px-3 py-2 text-xs border border-gray-100 rounded-lg bg-gray-50 text-gray-500 cursor-default select-none';

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5';

  // Renders a custom styled team dropdown using PortalDropdown
  const TeamDropdown = ({ field, users, btnRef, dropdownKey, label }) => {
    const selectedId = formData?.[field];
    const selectedLabel = getUserLabel(selectedId, users);
    const isOpen = openTeamDropdown === dropdownKey;

    return (
      <div>
        <label className={labelCls}>{label}</label>
        <button
          type="button"
          ref={btnRef}
          onClick={() => setOpenTeamDropdown(isOpen ? null : dropdownKey)}
          className={`${triggerCls} ${isOpen ? 'border-primary ring-1 ring-primary/20' : ''}`}
        >
          <span className={selectedLabel ? 'text-gray-900' : 'text-gray-400'}>
            {selectedLabel || t('projectTeam.selectUser')}
          </span>
          <Icon name="ChevronDown" size={12} className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        <PortalDropdown
          anchorRef={btnRef}
          open={isOpen}
          onClose={() => setOpenTeamDropdown(null)}
          width={220}
        >
          {/* Clear option */}
          <button
            type="button"
            onClick={() => { handleChange(field, null); setOpenTeamDropdown(null); }}
            className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 italic"
          >
            {t('projectTeam.selectUser')}
          </button>
          {users?.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400 italic">{t('projectTeam.noUsersAvailable')}</div>
          ) : (
            users?.map((u) => (
              <button
                key={u?.id}
                type="button"
                onClick={() => { handleChange(field, u?.id); setOpenTeamDropdown(null); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-primary/5 flex items-center gap-2 ${selectedId === u?.id ? 'font-semibold text-primary bg-primary/5' : 'text-gray-700'}`}
              >
                {selectedId === u?.id && <Icon name="Check" size={10} className="text-primary flex-shrink-0" />}
                <span className={selectedId === u?.id ? '' : 'ml-[14px]'}>{u?.full_name || u?.email}</span>
              </button>
            ))
          )}
        </PortalDropdown>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[700px] flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="Plus" size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {isEditMode ? t('newProjectModal.titleEdit') : t('newProjectModal.titleCreate')}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {isEditMode ? t('newProjectModal.subtitleEdit') : t('newProjectModal.subtitleCreate')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4">
          <div className="space-y-5">

            {/* Section: Project Identity */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionIdentity')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.projectName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.projectNamePlaceholder')}
                    value={formData?.name}
                    onChange={(e) => handleChange('name', e?.target?.value)}
                    className={inputCls}
                  />
                  {errors?.name && (
                    <p className="text-xs text-red-500 mt-1">{errors?.name}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.projectNumber')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.projectNumberPlaceholder')}
                    value={formData?.projectNumber}
                    onChange={(e) => handleChange('projectNumber', e?.target?.value)}
                    className={inputCls}
                  />
                  {errors?.projectNumber ? (
                    <p className="text-xs text-red-500 mt-1">{errors?.projectNumber}</p>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                      <Icon name="Info" size={10} className="text-gray-400" />
                      {t('newProjectModal.projectNumberHelper')}
                    </p>
                  )}
                </div>
              </div>

              {/* Client Project Number */}
              <div className="mt-4">
                <label className={labelCls}>
                  {t('newProjectModal.clientProjectNumber')}
                </label>
                <input
                  type="text"
                  placeholder={t('newProjectModal.clientProjectNumberPlaceholder')}
                  value={formData?.clientProjectNumber}
                  onChange={(e) => handleChange('clientProjectNumber', e?.target?.value)}
                  className={inputCls}
                />
                <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                  <Icon name="Info" size={10} className="text-gray-400" />
                  {t('newProjectModal.clientProjectNumberHelper')}
                </p>
              </div>
            </div>

            {/* Section: Client & Financials */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionClientFinancials')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.client')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.clientPlaceholder')}
                    value={formData?.clientName}
                    onChange={(e) => handleChange('clientName', e?.target?.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.contractValue')}
                  </label>
                  <div className={readOnlyCls}>
                    {isEditMode && initialData?.contractValue
                      ? fmtCAD(initialData?.contractValue)
                      : t('newProjectModal.contractValueAuto')}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                    <Icon name="Info" size={10} className="text-gray-400" />
                    {t('newProjectModal.contractValueHelper')}
                  </p>
                </div>
              </div>
            </div>

            {/* Section: Classification */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionClassification')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.constructionType')}
                  </label>
                  <SearchableSelect
                    options={constructionTypeOptions}
                    value={formData?.constructionType}
                    onChange={(value) => handleChange('constructionType', value || '')}
                    placeholder={t('newProjectModal.constructionTypePlaceholder')}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.workNature')}
                  </label>
                  <SearchableSelect
                    options={workNatureOptions}
                    value={formData?.workNature}
                    onChange={(value) => handleChange('workNature', value || '')}
                    placeholder={t('newProjectModal.workNaturePlaceholder')}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.architect')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.architectPlaceholder')}
                    value={formData?.architect}
                    onChange={(e) => handleChange('architect', e?.target?.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            {/* Section: Schedule */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionSchedule')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.startDate')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData?.startDate}
                    onChange={(e) => handleChange('startDate', e?.target?.value)}
                    className={inputCls}
                  />
                  {errors?.startDate && (
                    <p className="text-xs text-red-500 mt-1">{errors?.startDate}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.endDate')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData?.endDate}
                    onChange={(e) => handleChange('endDate', e?.target?.value)}
                    className={inputCls}
                  />
                  {errors?.endDate && (
                    <p className="text-xs text-red-500 mt-1">{errors?.endDate}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelCls}>{t('newProjectModal.timeframe')}</label>
                  <div className={readOnlyCls}>
                    {timeframe || '—'}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                    <Icon name="Info" size={10} className="text-gray-400" />
                    {t('newProjectModal.timeframeHelper')}
                  </p>
                </div>
              </div>
            </div>

            {/* Section: Location */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionLocation')}</p>
              <div className="mb-4">
                <label className={labelCls}>
                  {t('newProjectModal.address')}
                </label>
                <input
                  type="text"
                  placeholder={t('newProjectModal.addressPlaceholder')}
                  value={formData?.address}
                  onChange={(e) => handleChange('address', e?.target?.value)}
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.city')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.cityPlaceholder')}
                    value={formData?.city}
                    onChange={(e) => handleChange('city', e?.target?.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.state')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.statePlaceholder')}
                    value={formData?.state}
                    onChange={(e) => handleChange('state', e?.target?.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('newProjectModal.zipCode')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('newProjectModal.zipCodePlaceholder')}
                    value={formData?.zipCode}
                    onChange={(e) => handleChange('zipCode', e?.target?.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            {/* Section: Notes */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('newProjectModal.sectionNotes')}</p>
              <div>
                <label className={labelCls}>
                  {t('newProjectModal.description')}
                </label>
                <textarea
                  placeholder={t('newProjectModal.descriptionPlaceholder')}
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>

            {/* Section: Project Team — role-filtered custom dropdowns */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {t('projectTeam.sectionTitle')}
              </p>
              {teamLoading ? (
                <p className="text-xs text-gray-400">{t('projectTeam.picker.loading')}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Chargé de projet — role='pm' only */}
                  <TeamDropdown
                    field="pmUserId"
                    users={pmUsers}
                    btnRef={pmBtnRef}
                    dropdownKey="pm"
                    label={t('projectTeam.pm')}
                  />
                  {/* Coordonnateur — role='coordinator' only — writes to coordinator_user_id */}
                  <TeamDropdown
                    field="coordinatorUserId"
                    users={coordinatorUsers}
                    btnRef={coordinatorBtnRef}
                    dropdownKey="coordinator"
                    label={t('projectTeam.coordinator')}
                  />
                  {/* Superviseur — role='supervisor' only */}
                  <TeamDropdown
                    field="supervisorUserId"
                    users={supervisorUsers}
                    btnRef={supervisorBtnRef}
                    dropdownKey="supervisor"
                    label={t('projectTeam.supervisor')}
                  />
                  {/* Assistant(e) — role='assistant' only — writes to assistant_user_id */}
                  <TeamDropdown
                    field="assistantUserId"
                    users={assistantUsers}
                    btnRef={assistantBtnRef}
                    dropdownKey="assistant"
                    label={t('projectTeam.assistant')}
                  />
                </div>
              )}
            </div>

          </div>
        </form>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('newProjectModal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Icon name="Loader2" size={13} className="animate-spin" />
                {isEditMode ? t('newProjectModal.saving') : t('newProjectModal.creating')}
              </>
            ) : (
              <>
                <Icon name="Check" size={13} />
                {isEditMode ? t('newProjectModal.saveChanges') : t('newProjectModal.createProject')}
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default NewProjectModal;